/**
 * API Endpoints
 * Centralized API endpoint definitions for type safety and maintainability.
 */

/**
 * Authentication endpoints.
 */
export const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REGISTER: '/auth/register',
  REFRESH_TOKEN: '/auth/refresh',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email',
  ME: '/auth/me',
} as const;

/**
 * User management endpoints.
 */
export const USER_ENDPOINTS = {
  LIST: '/users',
  DETAIL: (id: string | number) => `/users/${id}`,
  CREATE: '/users',
  UPDATE: (id: string | number) => `/users/${id}`,
  DELETE: (id: string | number) => `/users/${id}`,
  PROFILE: '/users/profile',
  UPDATE_PROFILE: '/users/profile',
  CHANGE_PASSWORD: '/users/change-password',
  // Admin: List users for selection (e.g., memo custom group)
  LIST_FOR_SELECTION: '/users',
} as const;

/**
 * Registration module endpoints.
 */
export const REGISTRATION_ENDPOINTS = {
  LINKS: '/registration/links',
  LINK_DETAIL: (id: string | number) => `/registration/links/${id}`,
  CREATE_LINK: '/registration/links',
  REGISTRANTS: '/registration/registrants',
  REGISTRANT_DETAIL: (id: string | number) => `/registration/registrants/${id}`,
} as const;

/**
 * Dashboard Dai'e endpoints.
 */
export const DASHBOARD_DAIE_ENDPOINTS = {
  OVERVIEW: '/dashboard-daie/overview',
  CONTRACT: '/dashboard-daie/contract',
  KPI: '/dashboard-daie/kpi',
  COMMISSION: '/dashboard-daie/commission',
  NOTIFICATIONS: '/dashboard-daie/notifications',
} as const;

/**
 * Sales module endpoints.
 */
export const SALES_ENDPOINTS = {
  LEADS: '/sales/leads',
  LEAD_DETAIL: (id: string | number) => `/sales/leads/${id}`,
  APPOINTMENTS: '/sales/appointments',
  APPOINTMENT_DETAIL: (id: string | number) => `/sales/appointments/${id}`,
  PIPELINE: '/sales/pipeline',
  QUOTATIONS: '/sales/quotations',
  QUOTATION_DETAIL: (id: string | number) => `/sales/quotations/${id}`,
  CALENDAR: '/sales/calendar',
  TOOLS: '/sales/tools',
  PRODUCTS: '/sales/products',
  PRODUCTS_ACTIVE_FOR_LEADS: '/sales/products/active-for-leads',
} as const;

/**
 * Training/LMS module endpoints.
 */
export const TRAINING_ENDPOINTS = {
  COURSES: '/training/courses',
  COURSE_DETAIL: (id: string | number) => `/training/courses/${id}`,
  COURSE_ENROLLMENTS: (id: string | number) => `/training/courses/${id}/enrollments`,
  COURSE_ENROLLMENT_DELETE: (courseId: string | number, userId: number) =>
    `/training/courses/${courseId}/enrollments/${userId}`,
  COURSE_PROGRESS: (id: string | number) => `/training/courses/${id}/progress`,
  SUBMIT_QUIZ: (id: string | number) => `/training/courses/${id}/progress/quiz`,
  COMPLETE_COURSE: (id: string | number) => `/training/courses/${id}/progress/complete-course`,
  LESSON_NOTES: (courseId: string | number, lessonId: string | number) =>
    `/training/courses/${courseId}/lessons/${lessonId}/notes`,
  COURSE_CERTIFICATE: (id: string | number) => `/training/courses/${id}/certificate`,
  UPLOAD_DOCUMENT: '/training/upload-document',
  UPLOAD_CERTIFICATE_TEMPLATE: '/training/upload-certificate-template',
  UPLOAD_VIDEO: '/training/upload-video',
  PROGRESS: '/training/progress',
  CERTIFICATES: '/training/certificates',
  ATTENDANCE: '/training/attendance',
  QUIZZES: '/training/quizzes',
  QUIZ_SUBMIT: (id: string | number) => `/training/quizzes/${id}/submit`,
  PHYSICAL_PROGRAMS: '/training/physical-programs',
  PHYSICAL_PROGRAM_DETAIL: (id: string | number) => `/training/physical-programs/${id}`,
  PHYSICAL_PROGRAM_PARTICIPANTS: (id: string | number) => `/training/physical-programs/${id}/participants`,
  PHYSICAL_PROGRAM_RESULTS: (id: string | number) => `/training/physical-programs/${id}/results`,
  PHYSICAL_PROGRAM_QR_SESSION: (id: string | number) => `/training/physical-programs/${id}/qr-session`,
  PHYSICAL_PROGRAM_SCAN: (id: string | number) => `/training/physical-programs/${id}/scan`,
  PHYSICAL_PROGRAM_SCAN_INFO: (id: string | number) => `/training/physical-programs/${id}/scan-info`,
  MY_ATTENDANCE: '/training/my-attendance',
} as const;

/**
 * Executive dashboard endpoints.
 */
export const EXECUTIVE_ENDPOINTS = {
  DASHBOARD: '/executive/dashboard',
} as const;

/**
 * Dashboard Report endpoints.
 */
export const REPORT_ENDPOINTS = {
  AGENT_STATUS: '/reports/agent-status',
  CONTRACT_COMMISSION: '/reports/contract-commission',
  CONTRACT_REPORT: '/reports/contract-report',
  TRAINING_COMPLIANCE: '/reports/training-compliance',
  SALES_PERFORMANCE: '/reports/sales-performance',
  SALES_CONVERSION: '/reports/sales-conversion',
  PERFORMANCE: '/reports/performance',
  EXPORT: '/reports/export',
} as const;

/**
 * Memo module endpoints.
 */
export const MEMO_ENDPOINTS = {
  LIST: '/memos',
  DETAIL: (id: string | number) => `/memos/${id}`,
  CREATE: '/memos',
  UPLOAD_ATTACHMENT: '/memos/upload-attachment',
  SEND: (id: string | number) => `/memos/${id}/send`,
  MARK_READ: (id: string | number) => `/memos/${id}/read`,
  MARK_ALL_READ: '/memos/read-all',
  TEMPLATES: '/memos/templates',
  ANALYTICS: '/memos/analytics',
} as const;

/**
 * Notifications endpoints (Daie - memo deliveries for current user).
 */
export const NOTIFICATION_ENDPOINTS = {
  LIST: '/notifications',
  UNREAD_COUNT: '/notifications/unread-count',
} as const;

/**
 * Chatbot module endpoints.
 */
export const CHATBOT_ENDPOINTS = {
  // Conversations
  CONVERSATIONS: '/chatbot/conversations',
  CONVERSATION_DETAIL: (id: string | number) => `/chatbot/conversations/${id}`,
  SEND_MESSAGE: (id: string | number) => `/chatbot/conversations/${id}/messages`,

  // Starter Questions (public for authenticated users)
  STARTER_QUESTIONS: '/chatbot/starter-questions',

  // Admin: Documents
  DOCUMENTS: '/chatbot/documents',
  DOCUMENT_DETAIL: (id: string | number) => `/chatbot/documents/${id}`,

  // Admin: Configuration
  CONFIG: '/chatbot/config',

  // Admin: Starter Questions Management
  STARTER_QUESTIONS_ADMIN: '/chatbot/starter-questions/admin',
  STARTER_QUESTION_CREATE: '/chatbot/starter-questions',
  STARTER_QUESTION_DETAIL: (id: string | number) => `/chatbot/starter-questions/${id}`,
} as const;

/**
 * Support Inquiries endpoints.
 */
export const SUPPORT_INQUIRY_ENDPOINTS = {
  // Daie: submit inquiry
  SUBMIT: '/support-inquiries',
  // Admin: list all inquiries
  LIST: '/support-inquiries',
  // Admin: inquiry detail
  DETAIL: (id: string | number) => `/support-inquiries/${id}`,
  // Admin: reply to inquiry
  REPLY: (id: string | number) => `/support-inquiries/${id}/reply`,
} as const;

/**
 * File upload endpoints.
 */
export const FILE_ENDPOINTS = {
  UPLOAD: '/files/upload',
  DELETE: (id: string | number) => `/files/${id}`,
} as const;

/**
 * KPI targets: current user's targets (daie loads from DB).
 */
export const KPI_TARGETS_ENDPOINT = '/kpi-targets';

/**
 * Manager dashboard endpoints (manager_kde / manager_dpm).
 */
export const MANAGER_ENDPOINTS = {
  OVERVIEW: '/manager/overview',
  RECENT_ACTIVITY: '/manager/recent-activity',
  AGENT_MONITORING: '/manager/agent-monitoring',
  KPI_COMPLIANCE: '/manager/kpi-compliance',
  KPI_CONFIGURATION: '/manager/kpi-configuration',
  KPI_CONFIGURATION_IMPORT: '/manager/kpi-configuration/import',
} as const;

/**
 * Dai'e Management (admin) endpoints.
 */
export const DAIE_MANAGEMENT_ENDPOINTS = {
  LIST: '/daie-management',
  PROFILE: (userId: string | number) => `/daie-management/${userId}/profile`,
  BULK_UPDATE: '/daie-management/bulk-update',
  DESTROY: (userId: string | number) => `/daie-management/${userId}`,
} as const;

/**
 * Product Config (admin) endpoints.
 */
export const PRODUCT_CONFIG_ENDPOINTS = {
  LIST: '/product-config',
  SYNC: '/product-config/sync',
  BY_ID: (id: number) => `/product-config/${id}`,
} as const;

/**
 * Roles & Permissions (admin) endpoints.
 */
export const ROLE_ENDPOINTS = {
  LIST: '/roles',
  DETAIL: (id: number) => `/roles/${id}`,
  CREATE: '/roles',
  UPDATE: (id: number) => `/roles/${id}`,
  DELETE: (id: number) => `/roles/${id}`,
} as const;

export const PERMISSION_ENDPOINTS = {
  LIST: '/permissions',
  LIST_FLAT: '/permissions/list',
} as const;

export const SPONSOR_ASSIGNMENT_ENDPOINTS = {
  LIST: '/sponsor-assignments',
  UPDATE: (userId: number) => `/sponsor-assignments/${userId}`,
} as const;
